/**
 * ModulesTab Component (H.18)
 * 
 * Tab content for managing module-level permissions.
 * Shows module selector with inherited and direct permissions.
 * Includes modal for granting module access and revoke functionality.
 */

import { useEffect, useState } from 'react';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Badge, 
  Skeleton,
  Icon,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui';
import { SimpleSelect } from '@/components/ui/form-wrappers';
import { toast } from 'sonner';
import { User, Users, Shield, X, Target, ArrowUpFromLine } from 'lucide-react';
import { 
  useModules,
  useModulePermissions, 
  useGrantModulePermission, 
  useRevokeModulePermission,
  useAvailableSubjects,
} from '@/features/permissions/hooks';
import type { ModulePermission, ModulePermissionGrantInput } from '@/features/permissions/types';
import { getModuleLabelKey, humanizeModuleName } from '@/features/permissions/moduleMetadata';

const LEVEL_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  edit: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  operate: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  use: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  view: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  bin_view: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  emergency_stop: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

// =============================================================================
// Grant Module Permission Dialog
// =============================================================================

interface GrantModuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: string;
}

function GrantModulePermissionDialog({ open, onOpenChange, module }: GrantModuleDialogProps) {
  const { t } = useI18n();
  const grantPermission = useGrantModulePermission();
  const { data: subjects, isLoading: loadingSubjects } = useAvailableSubjects();

  const [subjectType, setSubjectType] = useState<'user' | 'group'>('user');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [level, setLevel] = useState<string>('view');

  // REQ-CHAT-020: para el módulo conversations el caso de uso principal es
  // otorgar la papelera — ponemos bin_view como opción primera y por defecto.
  useEffect(() => {
    if (open && module === 'conversations') {
      setLevel('bin_view');
    }
  }, [open, module]);

  const subjectOptions = subjects
    .filter((s) => (subjectType === 'group' ? s.type === 'group' : s.type === 'user'))
    .map((s) => ({
      value: s.id,
      label:
        subjectType === 'user'
          ? s.email || s.name || 'Email no disponible'
          : s.name,
    }));

  // ADR-174: analytics keeps its module entry but the dedicated Analytics
  // namespace only supports two levels (owners/admins by default; explicit
  // viewers are managed as `view`).
  const levelOptions =
    module === 'analytics'
      ? [
          { value: 'admin' as const, label: t('admin.permissions.levels.admin') },
          { value: 'view' as const, label: t('admin.permissions.levels.view') },
        ]
      : [
          // REQ-CHAT-020 — solo lectura de la papelera de conversaciones (módulo conversations)
          ...(module === 'conversations'
            ? [{ value: 'bin_view' as const, label: t('admin.permissions.levels.bin_view') }]
            : []),
          // REQ-FRT-013 — permiso específico de parada de ejecuciones (módulo frontier)
          ...(module === 'frontier'
            ? [
                {
                  value: 'emergency_stop' as const,
                  label: t('admin.permissions.levels.emergency_stop'),
                },
              ]
            : []),
          { value: 'admin', label: t('admin.permissions.levels.admin') },
          { value: 'edit', label: t('admin.permissions.levels.edit') },
          { value: 'operate', label: t('admin.permissions.levels.operate') },
          { value: 'use', label: t('admin.permissions.levels.use') },
          { value: 'view', label: t('admin.permissions.levels.view') },
        ];

  const handleSubmit = async () => {
    if (!selectedSubject) return;
    try {
      await grantPermission.mutateAsync({
        module,
        subject_id: selectedSubject,
        subject_type: subjectType,
        level: level as ModulePermissionGrantInput['level'],
      });
      toast.success(t('admin.permissions.modules.grantSuccess') || 'Permission granted');
      setSelectedSubject('');
      setLevel('view');
      onOpenChange(false);
    } catch {
      toast.error(t('admin.permissions.modules.grantError') || 'Failed to grant permission');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {t('admin.permissions.modules.grantTitle') || `Grant Access to "${module}"`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <SimpleSelect
            label={t('admin.permissions.modules.subjectType') || 'Subject Type'}
            options={[
              { value: 'group', label: t('admin.permissions.usersGroups.groupsTab') || 'Group' },
              { value: 'user', label: t('admin.permissions.usersGroups.usersTab') || 'User' },
            ]}
            value={subjectType}
            onValueChange={v => { setSubjectType(v as 'user' | 'group'); setSelectedSubject(''); }}
          />
          <SimpleSelect
            label={subjectType === 'group' 
              ? (t('admin.permissions.modules.selectGroup') || 'Select Group')
              : (t('admin.permissions.modules.selectUser') || 'Select User')}
            options={subjectOptions}
            value={selectedSubject}
            onValueChange={setSelectedSubject}
            placeholder="Choose..."
            disabled={loadingSubjects}
            required
          />
          <SimpleSelect
            label={t('admin.permissions.level') || 'Permission Level'}
            options={levelOptions}
            value={level}
            onValueChange={setLevel}
            required
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedSubject || grantPermission.isPending}
          >
            {grantPermission.isPending ? (
              <><Icon name="loader" size={16} className="animate-spin mr-1" /> Granting...</>
            ) : (
              t('admin.permissions.modules.grant') || 'Grant Access'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Permission Row
// =============================================================================

function ModulePermissionRow({ 
  perm, 
  module, 
  isInherited,
}: { 
  perm: ModulePermission; 
  module: string;
  isInherited: boolean;
}) {
  const { t } = useI18n();
  const revokePermission = useRevokeModulePermission();

  const handleRevoke = async () => {
    try {
      await revokePermission.mutateAsync({ module, subjectId: perm.subject_id });
      toast.success(t('admin.permissions.modules.revokeSuccess') || 'Permission revoked');
    } catch {
      toast.error(t('admin.permissions.modules.revokeError') || 'Failed to revoke permission');
    }
  };

  const SUBJECT_ICONS: Record<string, React.ElementType> = { user: User, group: Users, role: Shield };
  const SubjectIcon = SUBJECT_ICONS[perm.subject_type] || User;
  // Never render a raw UUID as a label (ISS-475). The backend enriches
  // subject_name; when it can't, show a neutral label instead.
  const subjectLabel =
    perm.subject_name ||
    (perm.subject_type === 'group'
      ? t('admin.permissions.modules.unnamedGroup') || 'Unnamed group'
      : t('admin.permissions.modules.unnamedUser') || 'Unnamed user');

  return (
    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
      <div className="flex items-center gap-2">
        <span>{<SubjectIcon className="h-4 w-4 text-gray-500" />}</span>
        <span className="text-sm">{subjectLabel}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${LEVEL_COLORS[perm.level] || LEVEL_COLORS.view}`}>
          {t(`admin.permissions.levels.${perm.level}`)}
        </span>
        {!isInherited && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={handleRevoke}
            disabled={revokePermission.isPending}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ModulesTab
// =============================================================================

export function ModulesTab() {
  const { t } = useI18n();
  const { data: modulesResponse, isLoading: loadingModules } = useModules();
  const modules = modulesResponse?.items || [];
  const [selectedModule, setSelectedModule] = useState('');
  const { data: permissions, isLoading } = useModulePermissions(selectedModule);
  const [showGrantDialog, setShowGrantDialog] = useState(false);

  useEffect(() => {
    if (!selectedModule && modules.length > 0) {
      setSelectedModule(modules[0].name);
    }
  }, [modules, selectedModule]);

  const moduleOptions = modules.map((module) => {
    const labelKey = getModuleLabelKey(module.name);
    const translated = labelKey ? t(labelKey) : module.name;
    return {
      value: module.name,
      label: !labelKey || translated === labelKey ? humanizeModuleName(module.name) : translated,
    };
  });

  // Separate inherited vs direct permissions
  const inheritedPermissions = permissions?.permissions?.filter(p => p.source === 'role') || [];
  const directPermissions = permissions?.permissions?.filter(p => p.source === 'direct') || [];

  return (
    <>
      <div className="space-y-6">
        {/* Module Selector */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('admin.permissions.modules.selectModule') || 'Select Module:'}
          </label>
          <SimpleSelect
            options={moduleOptions}
            value={selectedModule}
            onValueChange={setSelectedModule}
            className="w-64"
          />
        </div>

        {loadingModules || isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : modules.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No modules available.
          </div>
        ) : (
          <>
            {/* Inherited from Roles (Read-only) */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <ArrowUpFromLine className="h-4 w-4" />
                  {t('admin.permissions.modules.inherited') || 'Inherited from Roles'}
                </h4>
                <Badge variant="secondary">
                  {t('admin.permissions.modules.readonly') || 'Read-only'}
                </Badge>
              </div>
              
              {inheritedPermissions.length > 0 ? (
                <div className="space-y-2">
                  {inheritedPermissions.map((perm, idx) => (
                    <ModulePermissionRow 
                      key={`inherited-${idx}`} 
                      perm={perm} 
                      module={selectedModule} 
                      isInherited 
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.permissions.modules.noInherited') || 'No inherited permissions from roles'}
                </p>
              )}
            </div>

            {/* Direct Grants (Editable) */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  {t('admin.permissions.modules.direct') || 'Direct Grants'}
                </h4>
                <Button 
                  variant="outline" 
                  size="sm" 
                  leftIcon="plus"
                  onClick={() => setShowGrantDialog(true)}
                >
                  {t('admin.permissions.modules.addAccess') || 'Add Access'}
                </Button>
              </div>
              
              {directPermissions.length > 0 ? (
                <div className="space-y-2">
                  {directPermissions.map((perm, idx) => (
                    <ModulePermissionRow 
                      key={`direct-${idx}`} 
                      perm={perm} 
                      module={selectedModule}
                      isInherited={false}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.permissions.modules.noDirect') || 'No direct grants on this module'}
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Grant Module Permission Dialog */}
      <GrantModulePermissionDialog
        open={showGrantDialog}
        onOpenChange={setShowGrantDialog}
        module={selectedModule}
      />
    </>
  );
}

export default ModulesTab;
